import type { CustomColumn } from "@/types";

/** Backend list-hydrate include keys (see createOrderItemsListSearchQuery). */
export type OrderItemsListInclude =
  | "customer"
  | "owner"
  | "orderType"
  | "createdBy"
  | "orderCustomFields"
  | "orderItemCustomFields"
  | "currentFile"
  | "fileMetadata"
  | "preview"
  | "material"
  | "printer"
  | "scheme"
  | "layout"
  | "batch"
  | "children"
  | "parent"
  | "inkConsumption";

export type OrderItemsHydrateOptions = {
  includes: OrderItemsListInclude[];
  /** Order-entity custom field ids only (joined on order.customFieldValues). */
  orderCustomFieldIds: number[];
  /** OrderItem-entity custom field ids only (joined on orderItem.customFieldValues). */
  orderItemCustomFieldIds: number[];
};

/**
 * Map visible table columns to backend hydrate includes.
 * Always requests thumbnail-related data (currentFile + preview + children encryption flags).
 */
export function columnsToHydrateOptions(columns: CustomColumn[]): OrderItemsHydrateOptions {
  const includes = new Set<OrderItemsListInclude>(["currentFile", "preview", "children"]);
  const orderCustomFieldIds = new Set<number>();
  const orderItemCustomFieldIds = new Set<number>();

  for (const col of columns) {
    if (col.isJobColumn) {
      if (col.field === "inkConsumption") includes.add("inkConsumption");
      continue;
    }

    if (col.type === "custom" && col.customFieldId != null) {
      const id = Number(col.customFieldId);
      if (col.entity === "Order") {
        orderCustomFieldIds.add(id);
        includes.add("orderCustomFields");
      } else {
        orderItemCustomFieldIds.add(id);
        includes.add("orderItemCustomFields");
      }
      continue;
    }

    const field = col.field || "";
    if (field.startsWith("order.customer") || field === "order.customer.name") includes.add("customer");
    if (field.startsWith("order.owner")) includes.add("owner");
    if (field.startsWith("orderType") || field === "order.orderType.name") includes.add("orderType");
    if (field.includes("createdBy")) includes.add("createdBy");
    if (field.startsWith("material")) includes.add("material");
    if (field.startsWith("currentFile.metadata") || field.includes("ImageInfo")) {
      includes.add("currentFile");
      includes.add("fileMetadata");
    }
    if (field.startsWith("currentFile")) includes.add("currentFile");
    if (field.startsWith("batch")) includes.add("batch");
    if (field.startsWith("printer")) includes.add("printer");
    if (field.startsWith("scheme")) includes.add("scheme");
    if (field.startsWith("layout")) includes.add("layout");
    if (field.startsWith("parent")) includes.add("parent");
  }

  return {
    includes: [...includes].sort(),
    orderCustomFieldIds: [...orderCustomFieldIds].sort((a, b) => a - b),
    orderItemCustomFieldIds: [...orderItemCustomFieldIds].sort((a, b) => a - b),
  };
}
